/**
 * 专题产品卡加购区：Quick View、渠道下拉、结账热区。
 * 用法：UgProductCta.init(root, { cleanups })
 */
(function () {
  'use strict';

  const SCROLL_LOCK = 'ug-cta-dd-scroll-lock';
  const RETAIL_LINK_SEL = '.ug-product-cta__channel, [data-ug-cta-retail-direct], .ug-product-cta__opt';

  /**
   * 渠道链接所属的 CTA 区。下拉面板挂到 body 后，用 aria-controls 反查原按钮。
   *
   * @param {Element} link
   * @returns {Element}
   */
  function ctaZoneFromRetailLink(link) {
    const inCta = link.closest('[data-ug-product-cta]');
    if (inCta) return inCta;
    const panel = link.closest('[data-ug-buy-dropdown-panel]');
    if (!(panel instanceof HTMLElement) || !panel.id) return link;
    const esc =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function' ? CSS.escape(panel.id) : panel.id;
    const toggle = document.querySelector(`[data-ug-buy-dropdown-toggle][aria-controls="${esc}"]`);
    const zone = toggle instanceof Element ? toggle.closest('[data-ug-product-cta]') : null;
    return zone || panel;
  }

  /**
   * 渠道点击 → select_item（有商品 JSON）或 click_button（无商品时降级）。
   * 契约对齐 Nexode Pro：button_name retail_channel + channel_domain。
   *
   * @param {Element | null} el
   * @param {string} href
   */
  function trackRetailChannel(el, href) {
    const track = window.ugGa4Track;
    if (!track) return;
    const items =
      el && typeof track.itemsFromElement === 'function' ? track.itemsFromElement(el) || [] : [];
    const channel_domain =
      typeof track.parseChannelName === 'function' ? track.parseChannelName(href) : '';
    const moduleHost = el instanceof Element ? el.closest('[data-ga4-module]') : null;
    const module = moduleHost ? (moduleHost.getAttribute('data-ga4-module') || '').trim() : '';
    if (items.length && typeof track.selectItem === 'function') {
      try {
        track.selectItem({
          button_name: 'retail_channel',
          module: module || undefined,
          currency: track.getCurrency ? track.getCurrency() : 'USD',
          items,
          channel_domain,
        });
      } catch (_) {}
      return;
    }
    if (typeof track.clickButton === 'function') {
      try {
        track.clickButton({
          module,
          button_name: 'retail_channel',
          channel_domain,
        });
      } catch (_) {}
    }
  }

  /**
   * 全页只绑一次。面板可能已挂到 body，不能挂在 section 上。
   */
  function onDocRetailClick(e) {
    const target = e.target;
    if (!(target instanceof Element)) return;
    const link = target.closest(RETAIL_LINK_SEL);
    if (!(link instanceof HTMLAnchorElement)) return;
    const href = link.getAttribute('href') || '';
    if (!href || href === '#') return;
    trackRetailChannel(ctaZoneFromRetailLink(link), href);
  }

  document.addEventListener('click', onDocRetailClick, true);

  /**
   * 打开主题全局 Quick Add。
   *
   * @param {string} productPageUrl
   * @param {string} [productTitle]
   */
  async function openQuickAddModal(productPageUrl, productTitle) {
    if (!productPageUrl) return false;
    const dialogEl = document.getElementById('quick-add-dialog');
    const modalContent = document.getElementById('quick-add-modal-content');
    if (!dialogEl || !modalContent || typeof dialogEl.showDialog !== 'function') {
      window.location.href = productPageUrl;
      return false;
    }
    try {
      const [{ morph }, { isMobileBreakpoint }] = await Promise.all([
        import('@theme/morph'),
        import('@theme/utilities')
      ]);
      const response = await fetch(productPageUrl, { credentials: 'same-origin' });
      if (!response.ok) throw new Error('fetch product page failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const gridElement = html.querySelector('[data-product-grid-content]');
      if (!gridElement) throw new Error('no product grid content');
      const productGrid = /** @type {Element} */ (gridElement.cloneNode(true));
      if (isMobileBreakpoint() && productTitle) {
        const productDetails = productGrid.querySelector('.product-details');
        const productFormComponent = productGrid.querySelector('product-form-component');
        const variantPicker = productGrid.querySelector('variant-picker');
        const productPrice = productGrid.querySelector('product-price');
        if (productDetails && productFormComponent && variantPicker && productPrice) {
          const titleLink = document.createElement('a');
          titleLink.textContent = productTitle;
          titleLink.href = productPageUrl;
          const productHeader = document.createElement('div');
          productHeader.classList.add('product-header');
          productHeader.appendChild(titleLink);
          productHeader.appendChild(productPrice);
          productGrid.appendChild(productHeader);
          productGrid.appendChild(variantPicker);
          productGrid.appendChild(productFormComponent);
          productDetails.remove();
        }
      }
      morph(modalContent, productGrid);
      dialogEl.showDialog();
      return true;
    } catch (_) {
      window.location.href = productPageUrl;
      return false;
    }
  }

  /**
   * Buy Now Quick View + 缺货态 class。
   *
   * @param {ParentNode} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initBuyButtons(root, ctx) {
    const { cleanups } = ctx;
    const zones = root.querySelectorAll('[data-ug-product-cta]');

    const clearBuyLoading = (buyBtn) => {
      if (!(buyBtn instanceof HTMLElement)) return;
      buyBtn.classList.remove('loading');
      buyBtn.removeAttribute('aria-busy');
    };
    const clearAllBuyLoading = () => {
      root.querySelectorAll('[data-ug-cta-buy].loading').forEach(clearBuyLoading);
    };
    const onDialogClose = () => clearAllBuyLoading();
    const onCartUpdate = (e) => {
      if (e && e.detail && e.detail.data && e.detail.data.didError) return;
      clearAllBuyLoading();
    };
    document.addEventListener('dialog:close', onDialogClose, true);
    document.addEventListener('cart:update', onCartUpdate, true);
    cleanups.push(() => document.removeEventListener('dialog:close', onDialogClose, true));
    cleanups.push(() => document.removeEventListener('cart:update', onCartUpdate, true));

    zones.forEach((zone) => {
      if (!(zone instanceof HTMLElement)) return;
      zone.querySelectorAll('[data-ug-cta-buy][data-ug-cta-action="quickview"]').forEach((buyBtn) => {
        if (!(buyBtn instanceof HTMLButtonElement)) return;
        const onBuyNowClick = async (e) => {
          e.preventDefault();
          if (buyBtn.classList.contains('is-disabled') || buyBtn.classList.contains('loading')) return;
          const url = zone.getAttribute('data-ug-cta-qv-url')?.trim() || '';
          const title = zone.getAttribute('data-ug-cta-qv-title') || '';
          if (!url) return;
          buyBtn.classList.add('loading');
          buyBtn.setAttribute('aria-busy', 'true');
          await new Promise((r) => requestAnimationFrame(() => requestAnimationFrame(r)));
          try {
            const opened = await openQuickAddModal(url, title);
            if (!opened) clearBuyLoading(buyBtn);
          } catch (_) {
            clearBuyLoading(buyBtn);
          }
        };
        buyBtn.addEventListener('click', onBuyNowClick);
        cleanups.push(() => buyBtn.removeEventListener('click', onBuyNowClick));
      });
    });
  }

  /**
   * Buy Now = 直接结账时，点商品卡热区打开 Quick View。
   *
   * @param {ParentNode} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initHotzones(root, ctx) {
    const { cleanups } = ctx;
    const zones = root.querySelectorAll('[data-ug-cta-hotzone]');
    zones.forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      const onClick = async (e) => {
        const url =
          el.getAttribute('data-ug-cta-qv-url')?.trim() ||
          el.closest('[data-ug-cta-hotzone-card]')?.querySelector('[data-ug-product-cta]')?.getAttribute('data-ug-cta-qv-url')?.trim() ||
          '';
        const title =
          el.getAttribute('data-ug-cta-qv-title') ||
          el.closest('[data-ug-cta-hotzone-card]')?.querySelector('[data-ug-product-cta]')?.getAttribute('data-ug-cta-qv-title') ||
          '';
        if (!url) return;
        e.preventDefault();
        await openQuickAddModal(url, title);
      };
      el.addEventListener('click', onClick);
      cleanups.push(() => el.removeEventListener('click', onClick));
    });
  }

  /**
   * 站外渠道下拉：桌面 hover，手机全屏弹层。
   *
   * @param {ParentNode} root
   * @param {{ cleanups: Array<() => void> }} ctx
   */
  function initDropdowns(root, ctx) {
    const { cleanups } = ctx;
    const wraps = root.querySelectorAll('[data-ug-buy-dropdown]');
    if (!wraps.length) return;

    const mqDesk = typeof window.matchMedia === 'function' ? window.matchMedia('(min-width: 1024px)') : null;
    const mqMobile = typeof window.matchMedia === 'function' ? window.matchMedia('(max-width: 1023.98px)') : null;
    const prefersHoverOpen =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(hover: hover) and (pointer: fine)').matches &&
      (!mqDesk || mqDesk.matches);

    const hoverLeaveTimers = new WeakMap();
    const modalOwner = new WeakMap();
    const panelOwner = new WeakMap();

    const restoreModal = (modalEl) => {
      if (!(modalEl instanceof HTMLElement)) return;
      const owner = modalOwner.get(modalEl);
      if (owner instanceof HTMLElement && modalEl.parentNode === document.body) {
        owner.appendChild(modalEl);
      }
    };

    const clearDeskPanelPos = (panelEl) => {
      if (!(panelEl instanceof HTMLElement)) return;
      panelEl.style.removeProperty('position');
      panelEl.style.removeProperty('left');
      panelEl.style.removeProperty('top');
      panelEl.style.removeProperty('right');
      panelEl.style.removeProperty('bottom');
      panelEl.style.removeProperty('width');
      panelEl.style.removeProperty('z-index');
      panelEl.removeAttribute('data-ug-cta-dd-portaled');
      panelEl.removeAttribute('data-ug-cta-dd-open');
    };

    const restorePanel = (panelEl) => {
      if (!(panelEl instanceof HTMLElement)) return;
      const owner = panelOwner.get(panelEl);
      clearDeskPanelPos(panelEl);
      if (owner instanceof HTMLElement && panelEl.parentNode === document.body) {
        try {
          owner.appendChild(panelEl);
        } catch (_) {}
      }
    };

    const positionDeskPanel = (panelEl, toggleEl) => {
      if (!(panelEl instanceof HTMLElement) || !(toggleEl instanceof HTMLElement)) return;
      const r = toggleEl.getBoundingClientRect();
      const width = Math.max(Math.round(r.width), 200);
      const gap = 8;
      const panelH = Math.max(panelEl.offsetHeight || 0, 96);
      const spaceBelow = window.innerHeight - r.bottom;
      const openUp = spaceBelow < panelH + gap && r.top > panelH + gap;
      panelEl.style.position = 'fixed';
      panelEl.style.left = `${Math.round(r.left)}px`;
      panelEl.style.width = `${width}px`;
      panelEl.style.right = 'auto';
      panelEl.style.zIndex = '2147483000';
      if (openUp) {
        panelEl.style.top = 'auto';
        panelEl.style.bottom = `${Math.round(window.innerHeight - r.top + gap)}px`;
      } else {
        panelEl.style.bottom = 'auto';
        panelEl.style.top = `${Math.round(r.bottom + gap)}px`;
      }
    };

    const mountDeskPanel = (panelEl, toggleEl) => {
      if (!(mqDesk && mqDesk.matches)) return;
      if (!(panelEl instanceof HTMLElement) || !(toggleEl instanceof HTMLElement)) return;
      if (!panelOwner.has(panelEl)) panelOwner.set(panelEl, panelEl.parentElement);
      if (panelEl.parentNode !== document.body) document.body.appendChild(panelEl);
      panelEl.setAttribute('data-ug-cta-dd-portaled', '1');
      panelEl.removeAttribute('data-ug-cta-dd-open');
      positionDeskPanel(panelEl, toggleEl);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (panelEl.parentNode !== document.body) return;
          panelEl.setAttribute('data-ug-cta-dd-open', '1');
          positionDeskPanel(panelEl, toggleEl);
        });
      });
    };

    const setModalOpen = (modalEl, open) => {
      if (!(modalEl instanceof HTMLElement)) return;
      if (open) modalEl.setAttribute('data-ug-cta-modal-open', '1');
      else modalEl.removeAttribute('data-ug-cta-modal-open');
    };

    const mountModalToBody = (modalEl, ownerWrap) => {
      if (!(modalEl instanceof HTMLElement) || !(ownerWrap instanceof HTMLElement)) return;
      if (!(mqMobile && mqMobile.matches)) return;
      modalOwner.set(modalEl, ownerWrap);
      if (modalEl.parentNode !== document.body) document.body.appendChild(modalEl);
    };

    const clearScrollLock = () => {
      try {
        document.documentElement.classList.remove(SCROLL_LOCK);
        document.body.classList.remove(SCROLL_LOCK);
      } catch (_) {}
    };

    const applyScrollLock = () => {
      if (mqMobile && mqMobile.matches) {
        try {
          document.documentElement.classList.add(SCROLL_LOCK);
          document.body.classList.add(SCROLL_LOCK);
        } catch (_) {}
      }
    };

    const clearHoverTimer = (wEl) => {
      if (!(wEl instanceof HTMLElement)) return;
      const tid = hoverLeaveTimers.get(wEl);
      if (tid != null) window.clearTimeout(tid);
      hoverLeaveTimers.delete(wEl);
    };

    const instances = [];
    wraps.forEach((wrap) => {
      if (!(wrap instanceof HTMLElement)) return;
      const toggle = wrap.querySelector('[data-ug-buy-dropdown-toggle]');
      const panel = wrap.querySelector('[data-ug-buy-dropdown-panel]');
      const modal = wrap.querySelector('[data-ug-buy-dropdown-modal]');
      if (!(toggle instanceof HTMLElement) || !(panel instanceof HTMLElement)) return;
      instances.push({
        wrap,
        toggle,
        panel,
        modal: modal instanceof HTMLElement ? modal : null,
        modalDismiss:
          modal instanceof HTMLElement ? modal.querySelector('[data-ug-buy-dropdown-modal-dismiss]') : null,
        modalClose: modal instanceof HTMLElement ? modal.querySelector('[data-ug-buy-dropdown-modal-close]') : null
      });
    });

    const closeAll = () => {
      instances.forEach(({ wrap, toggle, panel, modal }) => {
        clearHoverTimer(wrap);
        if (modal) {
          setModalOpen(modal, false);
          restoreModal(modal);
        }
        restorePanel(panel);
        wrap.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        if (panel.dataset.ugCtaDdMounted !== '1') panel.hidden = true;
      });
      clearScrollLock();
    };

    if (mqDesk) {
      const onDesk = () => {
        if (mqDesk.matches) closeAll();
      };
      mqDesk.addEventListener('change', onDesk);
      cleanups.push(() => mqDesk.removeEventListener('change', onDesk));
    }

    instances.forEach(({ wrap, toggle, panel, modal, modalDismiss, modalClose }) => {
      wrap.classList.remove('is-open');
      toggle.setAttribute('aria-expanded', 'false');
      panel.removeAttribute('hidden');
      panel.dataset.ugCtaDdMounted = '1';

      const close = () => {
        clearHoverTimer(wrap);
        if (modal) {
          setModalOpen(modal, false);
          restoreModal(modal);
        }
        restorePanel(panel);
        wrap.classList.remove('is-open');
        toggle.setAttribute('aria-expanded', 'false');
        clearScrollLock();
      };

      const open = () => {
        if (toggle.classList.contains('is-disabled')) return;
        if (wrap.classList.contains('is-open')) {
          clearHoverTimer(wrap);
          return;
        }
        clearHoverTimer(wrap);
        closeAll();
        wrap.classList.add('is-open');
        toggle.setAttribute('aria-expanded', 'true');
        applyScrollLock();
        if (modal) {
          setModalOpen(modal, true);
          mountModalToBody(modal, wrap);
        }
        mountDeskPanel(panel, toggle);
      };

      if (prefersHoverOpen) {
        const scheduleClose = () => {
          clearHoverTimer(wrap);
          const tid = window.setTimeout(() => {
            hoverLeaveTimers.delete(wrap);
            if (wrap.matches(':hover') || panel.matches(':hover')) return;
            close();
          }, 160);
          hoverLeaveTimers.set(wrap, tid);
        };
        const onEnter = () => {
          if (toggle.classList.contains('is-disabled')) return;
          clearHoverTimer(wrap);
          open();
        };
        const onPanelEnter = () => clearHoverTimer(wrap);
        wrap.addEventListener('mouseenter', onEnter);
        wrap.addEventListener('mouseleave', scheduleClose);
        panel.addEventListener('mouseenter', onPanelEnter);
        panel.addEventListener('mouseleave', scheduleClose);
        cleanups.push(() => wrap.removeEventListener('mouseenter', onEnter));
        cleanups.push(() => wrap.removeEventListener('mouseleave', scheduleClose));
        cleanups.push(() => panel.removeEventListener('mouseenter', onPanelEnter));
        cleanups.push(() => panel.removeEventListener('mouseleave', scheduleClose));
        const onToggleClickHover = (e) => {
          e.stopPropagation();
          if (toggle.classList.contains('is-disabled')) return;
          if (wrap.classList.contains('is-open')) close();
          else open();
        };
        toggle.addEventListener('click', onToggleClickHover);
        cleanups.push(() => toggle.removeEventListener('click', onToggleClickHover));
      } else {
        const onToggleClick = (e) => {
          e.preventDefault();
          e.stopPropagation();
          if (toggle.classList.contains('is-disabled')) return;
          if (wrap.classList.contains('is-open')) close();
          else open();
        };
        toggle.addEventListener('click', onToggleClick);
        cleanups.push(() => toggle.removeEventListener('click', onToggleClick));
      }

      const onDismiss = (e) => {
        e.preventDefault();
        e.stopPropagation();
        close();
      };
      if (modalDismiss instanceof HTMLElement) {
        modalDismiss.addEventListener('click', onDismiss);
        cleanups.push(() => modalDismiss.removeEventListener('click', onDismiss));
      }
      if (modalClose instanceof HTMLElement) {
        modalClose.addEventListener('click', onDismiss);
        cleanups.push(() => modalClose.removeEventListener('click', onDismiss));
      }
    });

    const onDocPointerDown = (e) => {
      const el = e.target instanceof Element ? e.target : null;
      if (
        !el ||
        el.closest('[data-ug-buy-dropdown]') ||
        el.closest('[data-ug-buy-dropdown-modal]') ||
        el.closest('[data-ug-buy-dropdown-panel]')
      ) {
        return;
      }
      closeAll();
    };
    document.addEventListener('pointerdown', onDocPointerDown, true);
    cleanups.push(() => document.removeEventListener('pointerdown', onDocPointerDown, true));
    const onKeyDown = (e) => {
      if (e.key === 'Escape') closeAll();
    };
    document.addEventListener('keydown', onKeyDown);
    cleanups.push(() => document.removeEventListener('keydown', onKeyDown));

    const repositionOpenPanels = () => {
      if (!(mqDesk && mqDesk.matches)) return;
      instances.forEach(({ wrap, toggle, panel }) => {
        if (!wrap.classList.contains('is-open')) return;
        if (panel.parentNode !== document.body) return;
        positionDeskPanel(panel, toggle);
      });
    };
    window.addEventListener('scroll', repositionOpenPanels, true);
    window.addEventListener('resize', repositionOpenPanels);
    cleanups.push(() => {
      window.removeEventListener('scroll', repositionOpenPanels, true);
      window.removeEventListener('resize', repositionOpenPanels);
    });

    cleanups.push(() => {
      instances.forEach(({ wrap, modal, panel }) => {
        clearHoverTimer(wrap);
        if (modal) {
          setModalOpen(modal, false);
          restoreModal(modal);
        }
        restorePanel(panel);
      });
      clearScrollLock();
    });
  }

  /**
   * 初始化加购区（传入任意包含产品卡的根节点）。
   *
   * @param {ParentNode} [root]
   * @param {{ cleanups?: Array<() => void> }} [options]
   * @returns {Array<() => void>}
   */
  function init(root, options) {
    const scope = root || document;
    const cleanups = (options && options.cleanups) || [];
    const ctx = { cleanups };
    initBuyButtons(scope, ctx);
    initHotzones(scope, ctx);
    initDropdowns(scope, ctx);
    return cleanups;
  }

  window.UgProductCta = {
    init,
    openQuickAddModal
  };
})();
